
import React, { forwardRef } from 'react';
import { LucideIcon } from 'lucide-react';

/**
 * BenefitCard component
 * Displays a single benefit with icon, title, description and metric
 */
interface BenefitCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  metric: string;
  metricLabel: string;
  index: number;
}

const BenefitCard = forwardRef<HTMLDivElement, BenefitCardProps>(
  ({ icon: Icon, title, description, metric, metricLabel, index }, ref) => {
    return (
      <div 
        ref={ref}
        className="p-6 md:p-8 rounded-xl border border-gray-700/30 bg-gray-800/20 backdrop-blur-sm transition-all duration-500 shadow-lg hover:shadow-blue-500/10 group opacity-0 transform translate-y-4"
        style={{ transitionDelay: `${index * 150}ms` }}
      >
        <div className="flex justify-center mb-6">
          {/* Circle around the icon */}
          <div className="p-6 rounded-full bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-gray-700/30 group-hover:scale-110 transition-transform duration-300">
            <Icon size={32} className="text-blue-400" strokeWidth={1.5} />
          </div>
        </div>
        
        <h3 className="text-xl font-semibold text-gray-100 mb-4 group-hover:text-blue-300 transition-colors duration-300 text-center">
          {title}
        </h3>
        
        <p className="text-gray-300 leading-relaxed mb-6 text-center">
          {description}
        </p>
        
        <div className="mt-auto pt-4 border-t border-gray-700/30">
          <div className="flex flex-col items-center">
            <span className="text-2xl font-bold text-blue-400 group-hover:text-blue-300 transition-colors duration-300">
              {metric}
            </span>
            <span className="text-sm text-gray-400 mt-1">
              {metricLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

BenefitCard.displayName = 'BenefitCard';

export default BenefitCard;
